<!-- Styling is Tailwind, per project convention. The Figma SIZE/SPACING values here are FIXED spec values, not
     brand tokens, so they are written as arbitrary values (`text-[24px]`, not `text-2xl`): each brand preset
     overrides Tailwind's `fontSize`/`spacing`/`borderRadius` scales, so a scale class would render differently
     per brand. `font-display`/`font-body` are the deliberate exceptions — those SHOULD follow the brand.
     COLOUR, by contrast, IS brand-driven (EPS-4309): the primary button gets its fill/text from each host's
     button system (`.primary-button` in core-mkt, global `button { @apply cta-button }` in xcc-client), and the
     accent (link/error/copy text + pill border) reads `var(--raf-accent, #db306a)` — core-mkt sets `--raf-accent`
     per brand (DEC gold, Ace pink); the fallback keeps Ace/XCC unchanged where the var is unset.
     A few rules cannot be expressed as utilities and remain in the SCSS (spinner keyframes + colour,
     `font-feature-settings`, the fa-icon `svg` descendant sizing, and the CKEditor rich-text descendants). -->
@if (variant() === 'modal') {
  <div
    class="raf-overlay fixed inset-0 z-[999] flex items-center justify-center bg-[#21333f]/30"
    [class.raf-overlay--ace]="designMode() === 'ace'"
    >
    <!-- `.raf-overlay--ace` carries no rules of its own — `rounded-[var(--radius-ace-mode,6px)]` on the card
         already handles the ace-mode geometry difference. It stays as a hook for a future override. -->
    <div
      #rafCard
      class="raf-card relative bg-white rounded-[var(--radius-ace-mode,6px)] shadow-[0_20px_40px_rgba(33,51,63,0.2)] w-[576px] max-w-[calc(100vw_-_32px)] pt-[40px] pb-[32px] px-[32px]"
      role="dialog"
      aria-modal="true"
      [attr.aria-label]="dialogLabel()"
      >
      <button
        type="button"
        class="raf-close absolute top-6 right-6 text-[#21333f] leading-[0] cursor-pointer"
        (click)="onClose()"
        aria-label="Close"
        >
        <fa-icon [icon]="faTimes"></fa-icon>
      </button>

      <ng-container [ngTemplateOutlet]="cardContent"></ng-container>
    </div>
  </div>
} @else {
  <div
    class="raf-card raf-card--inline relative bg-white rounded-[var(--radius-ace-mode,6px)] shadow-[0_20px_40px_rgba(33,51,63,0.2)] max-w-[799px] mx-auto p-[32px]"
    >
    <ng-container [ngTemplateOutlet]="cardContent"></ng-container>
  </div>
}

<ng-template #cardContent>
  @if (screen() === 'loading') {
    <div class="raf-loading flex items-center justify-center min-h-[160px]" role="status" aria-label="Loading">
      <span
        class="raf-spinner w-[40px] h-[40px] rounded-full border-[3px]"
        aria-hidden="true"
      ></span>
    </div>
  }

  @if (screen() === 'intake') {
    <div class="raf-intake text-center flex flex-col gap-2">
      <!-- Guarded so a blanked title renders no empty heading: a host (core-mkt Modal Message) can replace
           the intake title+body with a single authored rich block set only on `body`, leaving `title` ''. -->
      @if (copy().title) {
        <h2
          class="raf-title font-display font-[800] text-[24px] leading-[30px] text-[#21333f] mb-0"
          [innerHTML]="copy().title"
        ></h2>
      }
      <p
        class="raf-body font-body font-[400] text-[18px] leading-[27px] text-[#43646f]"
        [innerHTML]="copy().body"
      ></p>
      @if (copy().incentiveText) {
        <p
          class="raf-incentive font-body mt-3 font-[400] text-[18px] leading-[27px] text-[#43646f]"
          [innerHTML]="copy().incentiveText"
        ></p>
      }

      <form class="raf-form mt-4" [formGroup]="emailForm" (ngSubmit)="onSubmit()" novalidate>
        <label class="sr-only" for="raf-email-input">Email address</label>
        <input
          id="raf-email-input"
          type="email"
          class="raf-email-input w-[488px] max-w-full mx-auto block bg-[#f1f5f5] border-[1.5px] border-[#afc1c6] rounded-[12px] py-[17px] px-[12px] text-[16px] text-[#21333f] placeholder:text-[#43646f]"
          [class.raf-email-input--error]="showEmailError"
          formControlName="email"
          [placeholder]="copy().emailPlaceholder"
          autocomplete="email"
          [attr.aria-invalid]="showEmailError"
          [attr.aria-describedby]="showEmailError ? 'raf-email-error' : null"
          />

        @if (showEmailError) {
          <p
            id="raf-email-error"
            class="raf-email-error font-body mt-2 text-[color:var(--raf-accent,#db306a)] text-[14px] leading-[21px]"
            role="alert"
            [innerHTML]="copy().invalidEmailMessage"
          ></p>
        }

        @if (error()) {
          <p
            class="raf-api-error font-body mt-2 text-[color:var(--raf-accent,#db306a)] text-[14px] leading-[21px]"
            role="alert"
          >
            {{ error() }}
          </p>
        }

        <!-- Colour comes from each host's brand button system, not a hardcoded hex: core-mkt styles
             `.primary-button` (bg-visBtn-primary + text-vis-reverse-button, both per-brand); xcc-client
             has no `.primary-button` but its global `button { @apply cta-button }` colours it per-brand.
             Both give gold+dark on DEC / pink+white on Ace. `rounded-[999px] border-0` are pinned so the
             pill shape holds in XCC where `.cta-button` doesn't set them. -->
        <button
          type="submit"
          class="raf-cta primary-button font-body mt-4 w-full rounded-[999px] border-0 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          [disabled]="loading()"
          >
          {{ copy().ctaLabel }}
        </button>
      </form>
    </div>
  }

  @if (screen() === 'link') {
    <div class="raf-share text-center flex flex-col gap-2">
      <h2
        class="raf-share-heading font-display font-[800] text-[24px] leading-[25px] text-[#21333f] mb-0"
        [innerHTML]="copy().shareHeading"
      ></h2>

      @if (error()) {
        <div class="raf-share-error mt-6">
          <p class="font-body" role="alert">{{ error() }}</p>
          <button
            type="button"
            class="raf-retry font-body mt-4 text-[color:var(--raf-accent,#db306a)] font-[700] cursor-pointer bg-transparent border-0"
            (click)="onRetry()"
            [innerHTML]="copy().retryLabel"
          ></button>
        </div>
      } @else {
        <div
          class="raf-link-pill mt-4 flex items-center justify-between border-[1.5px] border-[color:var(--raf-accent,#db306a)] rounded-[16px] py-4.5 px-[12px] gap-[12px]"
          >
          <span
            class="raf-link-text font-body truncate font-[700] text-[14px] leading-[21px] text-[color:var(--raf-accent,#db306a)]"
            >{{
            referralLink()
          }}</span>
          <!-- The box is pinned deliberately. xcc-client applies `@apply cta-button` to EVERY `button` from a
               global `body { … }` block (apps/xcc-client/src/styles.scss), which hands this button
               `padding: 18px 32px` + `display:flex` + `text-lg` and inflated the pill to ~99px vs Vision's ~50px.
               core-mkt has no such rule, so the two apps diverged. `p-0`/`bg-transparent`/`border-0` neutralise
               it; any interactive element added to this shared widget must likewise pin its own box. -->
          <button
            type="button"
            class="raf-copy-btn text-[color:var(--raf-accent,#db306a)] leading-[0] cursor-pointer shrink-0 w-[24px] h-[24px] p-0 bg-transparent border-0 flex items-center justify-center"
            (click)="onCopyLink()"
            [attr.aria-label]="copied() ? copy().copiedLabel : copy().copyButtonLabel"
            >
            <fa-icon [icon]="faCopy"></fa-icon>
          </button>
        </div>

        @if (copied()) {
          <p
            class="raf-copied-confirmation font-body mt-2 text-[color:var(--raf-accent,#db306a)] text-[14px] leading-[21px]"
            role="status"
            >
            {{ copy().copiedLabel }}
          </p>
        }

        <div class="raf-social-row mt-4 flex items-center justify-center gap-4 sm:gap-12">
          @for (channel of shareChannels(); track channel.id) {
            <div class="raf-social-item flex flex-col items-center gap-2">
              <a
                class="raf-social-icon raf-social-icon--{{ channel.id }} {{
                  channel.iconClasses
                }} flex items-center justify-center w-10 h-10 sm:w-14 sm:h-14 rounded-full cursor-pointer no-underline text-[24px]"
                [style.background-color]="channel.bgColor || null"
                [href]="channel.href"
                target="_blank"
                rel="noopener noreferrer"
                [attr.aria-label]="channel.label"
                >
                @if (channel.iconUrl) {
                  <!-- Sized explicitly: a Craft/config-supplied image (legacy XCC path) has no intrinsic
                       constraint and would render at its natural size, overflowing the 40px/56px circle and
                       eating the 8px gap above the caption. `object-contain` keeps non-square icons undistorted. -->
                  <img
                    class="raf-social-img w-6 h-6 sm:w-8 sm:h-8 object-contain"
                    [src]="channel.iconUrl"
                    [alt]="channel.iconAlt || channel.label"
                  />
                } @else if (channel.icon) {
                  <fa-icon [icon]="channel.icon"></fa-icon>
                }
              </a>
              <!-- Visibility uses `hidden sm:block` plus a max-width media query in the SCSS — Tailwind's
                   `max-*` arbitrary variants are unavailable in core-mkt (its preset has object `screens`). -->
              <span class="raf-social-label hidden sm:block text-[12px] text-[#43646f]">{{ channel.label }}</span>
            </div>
          }
        </div>
      }
    </div>
  }
</ng-template>
